import type { ReactNode } from 'react';
import { clsx } from '../../../ds/clsx';
import styles from './ProgressBanner.module.css';

export interface ProgressBannerProps {
  done: number;
  total: number;
  /** Eyebrow („Organisation eingerichtet", „Einrichtung fast fertig"). */
  eyebrow: string;
  title: string;
  subline?: ReactNode | undefined;
  /** navy: Admin 11 (fraction above the bar) · light: Nutzer 13 (bar, fraction, „Ausblenden" in one row). */
  tone: 'navy' | 'light';
  /** „Ausblenden"; the button is drawn only with a handler. */
  onHide?: (() => void) | undefined;
}

/** The checklist banner with the fraction „4 / 7" and the 6 px progress bar. */
export function ProgressBanner({ done, total, eyebrow, title, subline, tone, onHide }: ProgressBannerProps) {
  const percent = total > 0 ? Math.round((Math.min(done, total) / total) * 100) : 0;
  const fraction = (
    <span className={styles.fraction} aria-hidden="true">
      {done} / {total}
    </span>
  );
  const bar = (
    <span
      className={styles.track}
      role="progressbar"
      aria-label={`${done} von ${total} erledigt`}
      aria-valuemin={0}
      aria-valuemax={total}
      aria-valuenow={done}
    >
      <span className={styles.fill} style={{ width: `${percent}%` }} />
    </span>
  );
  const hide = onHide ? (
    <button type="button" className={styles.hide} onClick={onHide}>
      Ausblenden
    </button>
  ) : null;
  return (
    <section
      className={clsx(styles.banner, tone === 'navy' ? styles.navy : styles.light)}
      aria-label={eyebrow}
    >
      <div className={styles.text}>
        <span className={styles.eyebrow}>{eyebrow}</span>
        <h2 className={styles.title}>{title}</h2>
        {subline ? <span className={styles.subline}>{subline}</span> : null}
      </div>
      {tone === 'navy' ? (
        <div className={styles.progressNavy}>
          {fraction}
          {bar}
          {hide}
        </div>
      ) : (
        <div className={styles.progressLight}>
          {bar}
          {fraction}
          {hide}
        </div>
      )}
    </section>
  );
}
